import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Input } from '../../design-system';
import { AutoLink, PasLink, PersoonLink } from '../../components/ui/EntiteitLink';
import { Eyebrow } from '../../components/ui/Eyebrow';
import { Modal } from '../../components/ui/Modal';
import { ZoekBalk } from '../../components/ui/ZoekBalk';
import { ApiError } from '../../lib/apiClient';
import { usePassen, useCreatePas } from './passen.api';

const COLS = '190px 120px 1fr 110px 130px';

export function PassenPage() {
  const navigate = useNavigate();
  const { data: passen, isLoading } = usePassen();
  const create = useCreatePas();

  const [q, setQ] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [nieuwPas, setNieuwPas] = useState('');
  const [addError, setAddError] = useState('');

  async function onAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!nieuwPas.trim()) return;
    setAddError('');
    try {
      await create.mutateAsync(nieuwPas.trim());
      setShowAdd(false);
      setNieuwPas('');
    } catch (err) {
      setAddError(err instanceof ApiError ? err.message : 'Kon pas niet toevoegen');
    }
  }

  const filtered = useMemo(() => {
    const all = passen ?? [];
    const term = q.trim().toLowerCase();
    if (!term) return all;
    return all.filter((p) =>
      `${p.pasnummer} ${p.persoonNaam ?? ''} ${p.kenteken ?? ''}`.toLowerCase().includes(term),
    );
  }, [passen, q]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Eyebrow>Passen</Eyebrow>
          <h2 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 900, color: '#111111' }}>
            Tankpassen &amp; verbruik.
          </h2>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#666666' }}>
            Koppelen doe je via de persoon. Hier zie je kosten en gebruik per pas.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            variant="primary"
            onClick={() => {
              setAddError('');
              setNieuwPas('');
              setShowAdd(true);
            }}
          >
            + Pas toevoegen
          </Button>
          <Button variant="secondary" onClick={() => navigate('/passen/archief')}>
            Archief
          </Button>
        </div>
      </div>

      <ZoekBalk
        waarde={q}
        onWaarde={setQ}
        placeholder="Zoek op pasnummer, naam of kenteken…"
        ariaLabel="Passen zoeken"
        aantal={
          filtered.length === (passen?.length ?? 0)
            ? `${filtered.length} passen`
            : `${filtered.length} van ${passen?.length ?? 0}`
        }
      />

      <div
        style={{
          background: '#FFFFFF',
          border: '1px solid #E3D9CC',
          borderRadius: 4,
          overflowX: 'auto',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: COLS,
            gap: 10,
            padding: '12px 20px',
            borderBottom: '2px solid #111111',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: '#666666',
            minWidth: 760,
          }}
        >
          <span>Pasnummer</span>
          <span>Kenteken</span>
          <span>Naam</span>
          <span>Soort</span>
          <span>Status</span>
        </div>

        {isLoading && <div style={{ padding: '24px 20px', color: '#888888' }}>Passen laden…</div>}

        {filtered.map((p) => (
          <div
            key={p.id}
            onClick={() => navigate(`/passen/${p.id}`)}
            style={{
              display: 'grid',
              gridTemplateColumns: COLS,
              gap: 10,
              padding: '11px 20px',
              borderBottom: '1px solid #F0E8DC',
              fontSize: 13,
              alignItems: 'center',
              cursor: 'pointer',
              minWidth: 760,
            }}
          >
            <PasLink pasId={p.id} pasnummer={p.pasnummer} style={{ fontSize: 12 }} />
            <span style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 12 }}>
              {p.kenteken ? (
                <AutoLink kenteken={p.kenteken} />
              ) : (
                <span style={{ color: '#B4B4A9' }}>—</span>
              )}
            </span>
            <span>
              {p.persoonNaam ? (
                <PersoonLink persoonId={p.persoonId} naam={p.persoonNaam} />
              ) : (
                <span style={{ color: '#B4B4A9' }}>— niet gekoppeld —</span>
              )}
            </span>
            {/* Soort en status stonden in één kolom, waardoor een fictieve pas
                nooit liet zien of hij gekoppeld was: het eerste dat paste won. */}
            <span>
              {p.fictief ? (
                <Badge tone="warning">Fictief</Badge>
              ) : (
                <Badge tone="success">Vast</Badge>
              )}
            </span>
            <span>
              {p.persoonId ? (
                <Badge tone="success">Gekoppeld</Badge>
              ) : (
                <Badge tone="neutral">Ongekoppeld</Badge>
              )}
            </span>
          </div>
        ))}

        {!isLoading && filtered.length === 0 && (
          <div style={{ padding: '24px 20px', color: '#888888' }}>Geen passen gevonden.</div>
        )}
      </div>

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Pas toevoegen">
        <form onSubmit={onAdd} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input
            label="Pasnummer"
            placeholder="bv. 3081073919011806056"
            value={nieuwPas}
            onChange={(e) => setNieuwPas(e.target.value)}
            autoFocus
            required
          />
          <p style={{ margin: 0, fontSize: 12, color: '#888888' }}>
            Passen worden ook automatisch aangemaakt zodra ze in een MTC-uitdraai voorkomen.
            Koppelen aan een persoon doe je daarna via de persoon.
          </p>
          {addError && (
            <div style={{ fontSize: 13, color: '#B0543B', fontWeight: 700 }}>{addError}</div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button variant="ghost" type="button" onClick={() => setShowAdd(false)}>
              Annuleren
            </Button>
            <Button variant="primary" type="submit" disabled={create.isPending}>
              {create.isPending ? 'Bezig…' : 'Toevoegen'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
